import { Text, View } from "react-native";
import {
  formatIdentification,
  IdentificationTypeEnum,
} from "app/services/api/types/residents/enums/IdentificationTypeEnum";
import KeyValueGrid from "ui/organisms/KeyValueGrid";
import { useMemo, useRef } from "react";
import { convertDateToDDMMYYYY } from "ui/utils/dateUtils";
import { useResidentContext } from "../../provider/ResidentProvider";
import { EditResidentBioModal } from "../modals/EditResidentBioModal";
import { EditButton } from "../../molecules/EditButton";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";

export default function ResidentBioTemplate() {
  const { resident } = useResidentContext();
  const ref = useRef<React.ComponentRef<typeof EditResidentBioModal>>(null);
  const startEdit = () => {
    ref.current?.show(resident);
  };

  function mapPhotoConsent(consent?: boolean): string {
    if (consent === true) return "Yes";
    if (consent === false) return "No";
    return "N/A";
  }

  const residentBioItems = useMemo(
    () => [
      {
        label: "Identification Type/No.",
        value: formatIdentification(
          resident?.identification_type as IdentificationTypeEnum,
          resident?.identification_number,
        ),
      },
      { label: "Gender", value: resident?.gender || "N/A" },
      {
        label: "Date of Birth",
        value: resident?.date_of_birth
          ? `${convertDateToDDMMYYYY(resident.date_of_birth)} - ${resident.age}yrs`
          : "N/A",
      },
      {
        label: "Outside Pass Status",
        value: resident?.outside_pass_status || "N/A",
      },
      {
        label: "Outside Pass Status Details",
        value: resident?.outside_pass_status_details || "N/A",
      },
      {
        label: "Marital Status",
        value:
          (resident?.marital_status === "other"
            ? resident?.marital_status_description
              ? `${resident?.marital_status_description} (Other)`
              : "Other"
            : resident?.marital_status) || "N/A",
      },
      {
        label: "Nationality",
        value:
          resident?.nationality?.nationality ||
          resident?.nationality?.name ||
          "N/A",
      },
      {
        label: "Religion",
        value: resident?.religion || "N/A",
      },
      {
        label: "Former Occupation",
        value: resident?.former_occupation || "N/A",
      },
      {
        label: "Photography/Filming Consent",
        value: mapPhotoConsent(resident?.photo_consent),
      },
    ],
    [resident],
  );

  const nextOfKinSections = useMemo(() => {
    if (!resident?.relatives?.length) return null;

    return resident.relatives.map((relative, index) => ({
      subtitle: `Next of Kin ${String.fromCharCode(65 + index)}`,
      items: [
        {
          label: "Full Name",
          value:
            `${relative.first_name ?? ""} ${relative.last_name ?? ""}`.trim() ||
            "N/A",
        },
        { label: "Relationship", value: relative.relationship || "N/A" },
        { label: "Contact No.", value: relative.contact_number || "N/A" },
        { label: "Address", value: relative.address || "N/A" },
      ],
    }));
  }, [resident?.relatives]);

  return (
    <View className="resident-container">
      <EditButton
        startEdit={startEdit}
        permission={PermissionsEnums.UPDATE_RESIDENT}
      />
      <KeyValueGrid items={residentBioItems} />
      <View className="border-b border-gray-200 my-12" />
      <Text className="font-semibold mb-4">Next of Kin</Text>
      {nextOfKinSections ? (
        nextOfKinSections.map(({ subtitle, items }) => (
          <View key={subtitle} className="mb-8">
            <Text className="text-primary-500 mb-4">{subtitle}</Text>
            <KeyValueGrid items={items} />
          </View>
        ))
      ) : (
        <Text className="text-typography-600">No relatives listed.</Text>
      )}

      <EditResidentBioModal ref={ref} />
    </View>
  );
}
